// src/pages/DataQuality/components/PlotOptions.js
import React, { useMemo, useCallback } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Badge,
} from "reactstrap";
import PropTypes from "prop-types";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { Tooltip } from "react-tooltip";

// Constants
const PLOT_TYPES = {
  READLEN_DIST: "readlen_dist",
  NUC_COMP: "nuc_comp",
  TRIP_PERIODICITY: "trip_periodicity",
  MAPPED_READS_PLOT: "mapped_reads_plot",
  HEATMAP: "heatmap",
  METAGENE_PLOT: "metagene_plot",
  CUSTOM_METAGENE: "custom_metagene",
  MRNA_DIST: "mrna_dist",
  MRNA_DIST_READLEN: "mrna_dist_readlen",
  CODON_OCCUPANCY: "codon_occupancy",
  CLUSTER: "cluster",
  QC_SUMMARY: "qc_summary",
  REPLICATE_COMP: "replicate_comp",
  UNMAPPED: "unmapped",
  TE: "te",
};

const COLOR_PALETTES = [
  { value: "gist_rainbow", label: "Rainbow" },
  { value: "Inferno", label: "Inferno" },
  { value: "Viridis", label: "Viridis" },
  { value: "Spectral", label: "Spectral" },
];

const CORRELATION_TYPES = [
  { value: "pearson", label: "Pearson" },
  { value: "spearman", label: "Spearman" },
];

const COUNT_TYPES = [
  { value: "gene_counts", label: "Gene" },
  { value: "tran_counts", label: "Transcript" },
];

const COUNT_RESULTS = [
  { value: "raw_count", label: "Raw counts" },
  { value: "rpkm", label: "RPKM" },
  { value: "tpm", label: "TPM" },
];

const NORMALIZATION_METHODS = [
  { value: "average_read_density", label: "Average read density" },
  { value: "normalise", label: "Normalise by read depth" },
  { value: "none", label: "None" },
];

const CUSTOM_METAGENE_NORMALIZATION_METHODS = [
  { value: "normalise", label: "Normalise by read depth" },
  { value: "none", label: "None" },
];

const SEARCH_REGIONS = [
  { value: "whole_gene", label: "Whole Gene" },
  { value: "five_leader", label: "5' Leader (5' UTR)" },
  { value: "cds", label: "CDS" },
  { value: "three_trailer", label: "3' trailer (3' UTR)" },
];

const PlotOptions = ({ plotType, settings, onUpdateSetting }) => {
  // ✅ Enhanced tooltip component following established pattern
  const InfoTooltip = ({ id, content, placement = "top" }) => (
    <>
      <AiOutlineInfoCircle
        title={content}
        style={{
          marginLeft: "4px",
          cursor: "help",
          color: "#6B7280",
          fontSize: "16px",
          verticalAlign: "middle",
          display: "inline-flex",
          alignItems: "center",
        }}
      />
    </>
  );

  // ✅ Consistent input styling matching GeneralSettings
  const inputStyle = {
    borderRadius: "6px",
    border: "1px solid #d1d5db",
    fontSize: "14px",
    padding: "8px 12px",
    backgroundColor: "#ffffff",
    transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
  };

  // ✅ Enhanced label styling for consistency
  const labelStyle = {
    fontSize: "14px",
    fontWeight: "500",
    color: "#374151",
    marginBottom: "6px",
    display: "flex",
    alignItems: "center",
  };

  // ✅ Enhanced NumberField with focus preservation and consistent styling
  const NumberField = useCallback(
    ({
      id,
      value,
      onChange,
      label,
      placeholder,
      min,
      max,
      tooltip,
      disabled = false,
      required = false,
      width = 12,
    }) => {
      const handleKeyDown = (e) => {
        if (e.key === "Backspace" && (value === "" || value == null)) {
          e.preventDefault();
          e.stopPropagation();
        }
      };

      const handleChange = (e) => {
        const newValue = e.target.value;
        if (newValue === "" || newValue === null) {
          onChange(id, "");
        } else {
          const parsedValue = parseInt(newValue, 10);
          if (!isNaN(parsedValue)) {
            onChange(id, parsedValue);
          } else {
            onChange(id, newValue);
          }
        }
      };

      return (
        <FormGroup className="mb-3">
          <Label for={id} style={labelStyle}>
            {label}:
            {tooltip && <InfoTooltip id={`tt-${id}`} content={tooltip} />}
          </Label>
          <Input
            type="number"
            id={id}
            value={value || ""}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            min={min}
            max={max}
            disabled={disabled}
            style={{
              ...inputStyle,
              ...(disabled && { backgroundColor: "#f9fafb", color: "#6b7280" }),
            }}
          />
        </FormGroup>
      );
    },
    []
  );

  // ✅ Enhanced TextField with focus preservation
  const TextField = useCallback(
    ({
      id,
      value,
      onChange,
      label,
      placeholder,
      type = "text",
      tooltip,
      rows,
      disabled = false,
      required = false,
      width = 12,
    }) => {
      console.log("value", value);
      const handleChange = (e) => {
        const newValue = e.target.value;
        onChange(id, newValue);
      };

      const handleKeyDown = (e) => {
        if (e.key === "Backspace" && (value === "" || value == null)) {
          e.preventDefault();
          e.stopPropagation();
        }
      };

      return (
        <FormGroup className="mb-3">
          <Label for={id} style={labelStyle}>
            {label}:
            {tooltip && <InfoTooltip id={`tt-${id}`} content={tooltip} />}
          </Label>
          <Input
            type={type}
            id={id}
            value={value || ""}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={rows}
            disabled={disabled}
            style={{
              ...inputStyle,
              ...(type === "textarea" && { minHeight: "80px" }),
              ...(disabled && { backgroundColor: "#f9fafb", color: "#6b7280" }),
            }}
          />
        </FormGroup>
      );
    },
    []
  );

  // ✅ Enhanced RadioGroup with consistent styling and better alignment
  const RadioGroup = useCallback(
    ({
      name,
      options,
      value,
      onChange,
      label,
      tooltip,
      inline = true,
      disabled = false,
    }) => (
      <FormGroup className="mb-3">
        {label && (
          <Label style={labelStyle}>
            {label}:
            {tooltip && <InfoTooltip id={`tt-${name}`} content={tooltip} />}
          </Label>
        )}
        <div className={`mt-2 ${inline ? "d-flex flex-wrap gap-3" : ""}`}>
          {options.map((option) => (
            <FormGroup
              check
              key={option.value}
              className={inline ? "form-check" : "form-check mb-2"}
            >
              <Input
                type="radio"
                name={name}
                value={option.value}
                checked={value === option.value}
                onChange={(e) => onChange(name, e.target.value)}
                className="form-check-input"
                id={`${name}_${option.value}`}
                disabled={disabled}
              />
              <Label
                check
                for={`${name}_${option.value}`}
                className="form-check-label"
              >
                {option.label}
              </Label>
            </FormGroup>
          ))}
        </div>
      </FormGroup>
    ),
    []
  );

  // ✅ Simplified to use RadioGroup with inline=false
  const RadioGroupVertical = useCallback(
    (props) => <RadioGroup {...props} inline={false} />,
    []
  );

  // ✅ Enhanced CheckboxField with consistent styling
  const CheckboxField = useCallback(
    ({ id, checked, onChange, label, tooltip, disabled = false }) => (
      <FormGroup check className="mb-3">
        <Input
          type="checkbox"
          id={id}
          checked={checked || false}
          onChange={(e) => onChange(id, e.target.checked)}
          className="form-check-input"
          disabled={disabled}
        />
        <Label
          check
          for={id}
          className="form-check-label"
          style={{ fontSize: "14px", fontWeight: "500", color: "#374151" }}
        >
          {label}
          {tooltip && <InfoTooltip id={`tt-${id}`} content={tooltip} />}
        </Label>
      </FormGroup>
    ),
    []
  );

  const NucleotideBounds = ({ settings, onUpdateSetting }) => {
    const ANCHOR_OPTIONS = [
      { value: "tx_start", label: "Transcript start" },
      { value: "cds_start", label: "CDS start" },
      { value: "cds_stop",  label: "CDS stop" },
      { value: "tx_end",    label: "Transcript end" },
    ];

    const AnchorSelect = ({ id, value }) => (
      <Input
        type="select"
        id={id}
        value={value || "cds_start"}
        onChange={(e) => onUpdateSetting(id, e.target.value)}
        bsSize="sm"
        style={{ minWidth: "100px", fontSize: "13px" }}
      >
        {ANCHOR_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Input>
    );

    const NucleotideInput = ({ id, value, placeholder = "0" }) => {
      const handleKeyDown = (e) => {
        if (e.key === "Backspace" && (value === "" || value == null)) {
          e.preventDefault();
          e.stopPropagation();
        }
      };
      return (
        <Input
          type="number"
          id={id}
          min={-10000}
          value={value ?? ""}
          onChange={(e) => onUpdateSetting(id, e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          bsSize="sm"
          style={{ minWidth: "60px", fontSize: "13px" }}
        />
      );
    };

    const LimitRow = ({ rowLabel, anchorId, signId, valueId, tooltipId }) => (
      <Row className="align-items-center mb-3">
        <Col lg={3} md={2} className="mb-2 mb-md-0">
          <Label htmlFor={anchorId} className="mb-0" style={{ fontWeight: 500 }}>
            {rowLabel}
            {typeof InfoTooltip !== "undefined" && (
              <InfoTooltip
                id={tooltipId}
                content={`${rowLabel}: choose an anchor, sign, and offset in nucleotides.`}
              />
            )}
          </Label>
        </Col>

        <Col xs={12} md={4} className="mb-2 mb-md-0">
          <AnchorSelect id={anchorId} value={settings[anchorId]} />
        </Col>

        <Col xs={12} md={4}>
          <div className="d-flex align-items-center gap-2">
            <NucleotideInput id={valueId} value={settings[valueId]} />
            <span style={{ fontSize: "13px", color: "#6b7280" }}>nucleotides</span>
          </div>
        </Col>
      </Row>
    );

    return (
      <div className="border rounded" style={{ padding: "16px", backgroundColor: "#fff" }}>
        <h6
          className="mb-3"
          style={{ fontSize: "14px", fontWeight: 500, color: "#374151" }}
        >
          Min/Max Limits
        </h6>

        {/* Header */}
        <Row
          className="align-items-center mb-3 pb-2"
          style={{ borderBottom: "1px solid #e5e7eb" }}
        >
          <Col xs={12} md={4}>
            <strong style={{ fontSize: "12px", color: "#6b7280" }}>Limit</strong>
          </Col>
          <Col xs={12} md={4}>
            <strong style={{ fontSize: "12px", color: "#6b7280" }}>Anchor</strong>
          </Col>
          <Col xs={12} md={4}>
            <strong style={{ fontSize: "12px", color: "#6b7280" }}>Offset</strong>
          </Col>
        </Row>

        <LimitRow
          rowLabel="Min Limit"
          anchorId="min_limit_anchor"
          valueId="min_cust_metagene_search_limit"
          tooltipId="min-limit-tooltip"
        />

        <LimitRow
          rowLabel="Max Limit"
          anchorId="max_limit_anchor"
          valueId="max_cust_metagene_search_limit"
          tooltipId="max-limit-tooltip"
        />
      </div>
    );
  };


  // ✅ Section header component for consistency
  const SectionHeader = useCallback(
    ({ title, icon = "ri-settings-3-line" }) => (
      <h6
        className="mb-3"
        style={{
          fontSize: "13px",
          fontWeight: "600",
          color: "#6b7280",
          textTransform: "uppercase",
          letterSpacing: "0.025em",
          display: "flex",
          alignItems: "center",
        }}
      >
        <i className={`${icon} me-2`} style={{ fontSize: "14px" }} />
        {title}
      </h6>
    ),
    []
  );

  // ✅ Comprehensive plot type rendering with improved alignment
  const renderOptionsForPlotType = useCallback(() => {
    switch (plotType) {
      case PLOT_TYPES.READLEN_DIST:
        return (
          <Row>
            <Col md={6}>
              <SectionHeader title="Display Options" />
              <CheckboxField
                id="ambig"
                checked={settings.ambig}
                onChange={onUpdateSetting}
                label="Allow ambiguous reads"
                tooltip="All mapped reads in a file contribute to this plot (whether they map ambiguously or not). This plot can be used to help determine if a file contains genuine riboseq reads or not. In general we would expect the readlength distribution to have a relatively narrow peak centered around ~30 nucleotides for genuine riboseq data."
              />
              <CheckboxField
                id="single_plt"
                checked={settings.single_plt}
                onChange={onUpdateSetting}
                label="Single plot"
                tooltip="If checked, a single plot will be returned regardless of how many files are selected."
              />

              <SectionHeader title="Region Selection" />
              <RadioGroupVertical
                name="custom_search_region"
                options={SEARCH_REGIONS}
                value={settings.custom_search_region}
                onChange={onUpdateSetting}
                label="mRNA region (optional)"
                tooltip="Select which region of the mRNA to select reads from."
              />
            </Col>
            <Col md={6}>
              <SectionHeader title="Parameters" />
              <Row>
                <Col md={6}>
                  <NumberField
                    id="minread"
                    value={settings.minread}
                    onChange={onUpdateSetting}
                    label="Min readlen"
                    placeholder="20"
                    min={1}
                    tooltip="The shortest read length displayed on the plot."
                    required
                  />
                </Col>
                <Col md={6}>
                  <NumberField
                    id="maxread"
                    value={settings.maxread}
                    onChange={onUpdateSetting}
                    label="Max readlen"
                    placeholder="35"
                    min={1}
                    tooltip="The longest read length displayed on the plot."
                    required
                  />
                </Col>
              </Row>

              <TextField
                id="max_y_val"
                value={settings.max_y_val}
                onChange={onUpdateSetting}
                label="Max y value"
                placeholder="Auto"
                tooltip="Set the max value on the y-axis, this can be useful if creating a panel of plots."
              />

              <TextField
                id="metagene_tranlist"
                value={settings.metagene_tranlist}
                onChange={onUpdateSetting}
                label="Transcript list (optional)"
                placeholder="Enter comma-separated transcript IDs"
                type="textarea"
                rows="3"
                tooltip="Enter a comma separated list of transcript ids to get a read length distribution plot based only on those transcripts."
              />
            </Col>
          </Row>
        );

      case PLOT_TYPES.NUC_COMP:
        return (
          <Row>
            <Col md={6}>
              <SectionHeader title="Analysis Settings" />
              <RadioGroup
                name="direction"
                options={[
                  { value: "fiveprime", label: "5'" },
                  { value: "threeprime", label: "3'" },
                ]}
                value={settings.direction}
                onChange={onUpdateSetting}
                label="Direction"
                tooltip="Whether the nucleotides are taken from the 5' end or 3' end of all reads."
              />

              <RadioGroup
                name="nuc_comp_type"
                options={[
                  { value: "per", label: "Percentage" },
                  { value: "nuc_comp_count", label: "Absolute" },
                ]}
                value={settings.nuc_comp_type}
                onChange={onUpdateSetting}
                label="Type"
                tooltip="This can be either shown as percentages (per) or raw counts (count). If 'per' is selected the plot will show the percentage of each nucleotide across all reads. If 'count' is selected the plot will show the raw counts of each nucleotide across all reads."
              />

              <CheckboxField
                id="single_plt"
                checked={settings.single_plt}
                onChange={onUpdateSetting}
                label="Single plot"
                tooltip="If checked, a single plot will be returned regardless of how many files are selected."
              />
            </Col>
            <Col md={6}>
              <SectionHeader title="Parameters" />
              <Row>
                <Col md={6}>
                  <NumberField
                    id="minread"
                    value={settings.minread}
                    onChange={onUpdateSetting}
                    label="Min readlen"
                    placeholder="20"
                    tooltip="The shortest read length displayed on the plot."
                    required
                  />
                </Col>
                <Col md={6}>
                  <NumberField
                    id="maxread"
                    value={settings.maxread}
                    onChange={onUpdateSetting}
                    label="Max readlen"
                    placeholder="35"
                    tooltip="The longest read length displayed on the plot."
                    required
                  />
                </Col>
              </Row>

              <TextField
                id="max_y_val"
                value={settings.max_y_val}
                onChange={onUpdateSetting}
                label="Max y value"
                placeholder="Auto"
                tooltip="Set the max value on the y-axis."
              />

              <RadioGroupVertical
                name="nuccomp_reads"
                options={[
                  { value: "mapped", label: "All mapped reads" },
                  { value: "unmapped", label: "All unmapped reads" },
                  { value: "inframe", label: "CDS inframe only" },
                  { value: "offrame", label: "CDS out of frame only" },
                ]}
                value={settings.nuccomp_reads}
                onChange={onUpdateSetting}
                label="Read type"
                tooltip="Select which type of reads to analyze."
              />
            </Col>
          </Row>
        );

      case PLOT_TYPES.TRIP_PERIODICITY:
        return (
          <Row>
            <Col md={6}>
              <SectionHeader title="Parameters" />
              <Row>
                <Col md={6}>
                  <NumberField
                    id="minread"
                    value={settings.minread}
                    onChange={onUpdateSetting}
                    label="Min readlen"
                    placeholder="20"
                    tooltip="The shortest read length displayed on the plot."
                    required
                  />
                </Col>
                <Col md={6}>
                  <NumberField
                    id="maxread"
                    value={settings.maxread}
                    onChange={onUpdateSetting}
                    label="Max readlen"
                    placeholder="35"
                    tooltip="The longest read length displayed on the plot."
                    required
                  />
                </Col>
              </Row>
            </Col>
            <Col md={6}>
              <SectionHeader title="Options" />
              <CheckboxField
                id="single_plt"
                checked={settings.single_plt}
                onChange={onUpdateSetting}
                label="Single plot"
                tooltip="If checked, a single plot will be returned regardless of how many files are selected."
              />
              <TextField
                id="max_y_val"
                value={settings.max_y_val}
                onChange={onUpdateSetting}
                label="Max y value"
                placeholder="Auto"
                tooltip="Set the max value on the y-axis."
              />
            </Col>
          </Row>
        );

      case PLOT_TYPES.MAPPED_READS_PLOT:
        return (
          <Row>
            <Col md={6}>
              <SectionHeader title="Display Options" />
              <CheckboxField
                id="per"
                checked={settings.per}
                onChange={onUpdateSetting}
                label="Display as percentage"
                tooltip="This plot shows a breakdown of reads into multiple categories for each transcriptome alignment (.bam) file. It does not include reads that have been removed owing to being too short (<20nt long) or mapped to e.g. rRNA, tRNA, etc. Categories are as follows: 'Unmapped': Number of reads which could not be mapped to the transcriptome. 'Ambiguous': the number of reads which mapped to more than one gene. 'Mapped noncoding': Number of reads mapped to regions not annotated as coding. 'Mapped coding': Number of reads mapped to annotated coding regions."
              />
            </Col>
          </Row>
        );

      case PLOT_TYPES.HEATMAP:
        return (
          <Row>
            <Col md={6}>
              <SectionHeader title="Display Settings" />
              <RadioGroup
                name="direction"
                options={[
                  { value: "fiveprime", label: "5 prime" },
                  { value: "threeprime", label: "3 prime" },
                ]}
                value={settings.direction}
                onChange={onUpdateSetting}
                label="Direction"
                tooltip="Select which direction to start reading nucleotides from."
              />

              <CheckboxField
                id="log_scale"
                checked={settings.log_scale}
                onChange={onUpdateSetting}
                label="Log scale"
                tooltip="If checked, counts will be log scale."
              />

              <CheckboxField
                id="apply_offset"
                checked={settings.apply_offset}
                onChange={onUpdateSetting}
                label="Apply offsets"
                tooltip="If checked, offsets (to infer A-sites) will be applied to each read. If unchecked, read positions will be based on the exact 5' or 3' end position without A-site correction."
              />

              <CheckboxField
                id="reverse"
                checked={settings.reverse}
                onChange={onUpdateSetting}
                label="Reverse color scale"
                tooltip="If checked, color scale will be reversed."
              />

              <RadioGroup
                name="color_palette"
                options={COLOR_PALETTES}
                value={settings.color_palette}
                onChange={onUpdateSetting}
                label="Color palette"
                tooltip="Color palette to use for the color scale."
              />
            </Col>
            <Col md={6}>
              <SectionHeader title="Parameters" />
              <Row>
                <Col md={6}>
                  <NumberField
                    id="minread"
                    value={settings.minread}
                    onChange={onUpdateSetting}
                    label="Min readlen"
                    placeholder="20"
                    tooltip="The shortest read length displayed on the plot."
                    required
                  />
                </Col>
                <Col md={6}>
                  <NumberField
                    id="maxread"
                    value={settings.maxread}
                    onChange={onUpdateSetting}
                    label="Max readlen"
                    placeholder="50"
                    tooltip="The longest read length displayed on the plot."
                    required
                  />
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <NumberField
                    id="hmap_strt"
                    value={settings.hmap_strt}
                    onChange={onUpdateSetting}
                    label="Start position"
                    placeholder="-150"
                    tooltip="Distance (in nucleotides), to display upstream of the start/stop codon."
                  />
                </Col>
                <Col md={6}>
                  <NumberField
                    id="hmap_stop"
                    value={settings.hmap_stop}
                    onChange={onUpdateSetting}
                    label="End position"
                    placeholder="150"
                    tooltip="Distance (in nucleotides), to display downstream of the start/stop codon."
                  />
                </Col>
              </Row>

              <TextField
                id="maxscaleval"
                value={settings.maxscaleval}
                onChange={onUpdateSetting}
                label="Max scale value"
                placeholder="Auto"
                tooltip="Max value to display on the color scale."
              />

              <RadioGroupVertical
                name="center_around"
                options={[
                  { value: "start_codons", label: "Heatmap start codon" },
                  { value: "stop_codons", label: "Heatmap stop codon" },
                  { value: "whole_transcript", label: "Entire transcript" },
                ]}
                value={settings.center_around}
                onChange={onUpdateSetting}
                label="Type"
                tooltip="Select whether to center the heat map around the start or stop codons."
              />

              <TextField
                id="metagene_tranlist"
                value={settings.metagene_tranlist}
                onChange={onUpdateSetting}
                label="Transcript list (optional)"
                placeholder="Enter comma-separated transcript IDs"
                type="textarea"
                rows="3"
                tooltip="Enter a comma separated list of transcript ids."
              />
            </Col>
          </Row>
        );

      case PLOT_TYPES.METAGENE_PLOT:
        return (
          <Row>
            <Col md={6}>
              <SectionHeader title="Display Settings" />
              <RadioGroup
                name="direction"
                options={[
                  { value: "fiveprime", label: "Include 5' ends" },
                  { value: "threeprime", label: "Include 3' ends" },
                ]}
                value={settings.direction}
                onChange={onUpdateSetting}
                label="Direction"
                tooltip="Choose which end of the read the counts are based on, only one option can be chosen at a time. Genuine Ribo-Seq data should have a much higher density of reads 3' of the start codon than 5' of the start codon (and vice versa for the stop codon)."
              />

              <CheckboxField
                id="apply_offset"
                checked={settings.apply_offset}
                onChange={onUpdateSetting}
                label="Apply offsets"
                tooltip="If checked the offsets used to infer the A-site will be applied to the read counts."
              />

              <CheckboxField
                id="aggregate_counts"
                checked={settings.aggregate_counts}
                onChange={onUpdateSetting}
                label="Aggregate counts"
                tooltip="If checked, the counts from all selected files will be grouped together, if unchecked files will be plotted with different colours seperately. A maximum of 6 files can be selected if not aggregating."
              />

              <Row>
                <Col md={6}>
                  <NumberField
                    id="hmap_strt"
                    value={settings.hmap_strt}
                    onChange={onUpdateSetting}
                    label="Start position"
                    placeholder="-300"
                    tooltip="Distance (in nucleotides), to display upstream of the start/stop codon."
                  />
                </Col>
                <Col md={6}>
                  <NumberField
                    id="hmap_stop"
                    value={settings.hmap_stop}
                    onChange={onUpdateSetting}
                    label="End position"
                    placeholder="300"
                    tooltip="Distance (in nucleotides), to display downstream of the start/stop codon."
                  />
                </Col>
              </Row>
            </Col>
            <Col md={6}>
              <SectionHeader title="Parameters" />
              <Row>
                <Col md={6}>
                  <NumberField
                    id="minread"
                    value={settings.minread}
                    onChange={onUpdateSetting}
                    label="Min readlen"
                    placeholder="20"
                    tooltip="The shortest read length displayed on the plot."
                    required
                  />
                </Col>
                <Col md={6}>
                  <NumberField
                    id="maxread"
                    value={settings.maxread}
                    onChange={onUpdateSetting}
                    label="Max readlen"
                    placeholder="35"
                    tooltip="The longest read length displayed on the plot."
                    required
                  />
                </Col>
              </Row>

              <TextField
                id="metagene_tranlist"
                value={settings.metagene_tranlist || ""}
                onChange={onUpdateSetting}
                label="Transcript list (optional)"
                placeholder=""
                type="textarea"
                rows="3"
                tooltip="Enter a comma separated list of transcript ids."
              />

              <RadioGroupVertical
                name="norm_method"
                options={NORMALIZATION_METHODS}
                value={settings.norm_method}
                onChange={onUpdateSetting}
                label="Normalisation method"
                tooltip="If checked, counts will be normalised based on the number of mapped reads in each file. This is based on a factor difference between the file with lowest number of mapped reads and all other files. For example if plotting three files A,B and C which have 100,200,300 mapped reads respectively. The counts from file A would be unchanged (as it has the lowest number of mapped reads), all counts from file B woudld be multiplied by a factor of 0.5 (100/200 = 0.5) and all counts from File C would be multiplied by a factor of 0.33 (100/300 = 0.33)."
              />

              <RadioGroupVertical
                name="center_around"
                options={[
                  { value: "start_codons", label: "Metagene start codon" },
                  { value: "stop_codons", label: "Metagene stop codon" },
                ]}
                value={settings.center_around}
                onChange={onUpdateSetting}
                label="Center around"
                tooltip="Select whether to center the metagene profile around the start/stop codons."
              />

            </Col>
          </Row>
        );

      case PLOT_TYPES.CUSTOM_METAGENE:
        return (
          <Row>
            <Col md={6}>
              <SectionHeader title="Display Settings" />
              <RadioGroup
                name="direction"
                options={[
                  { value: "fiveprime", label: "Include 5' ends" },
                  { value: "threeprime", label: "Include 3' ends" },
                ]}
                value={settings.direction}
                onChange={onUpdateSetting}
                label="Direction"
                tooltip="Choose which end of the read the counts are based on, only one option can be chosen at a time. Genuine Ribo-Seq data should have a much higher density of reads 3' of the start codon than 5' of the start codon (and vice versa for the stop codon)."
              />

              <CheckboxField
                id="apply_offset"
                checked={settings.apply_offset}
                onChange={onUpdateSetting}
                label="Apply offsets"
                tooltip="If checked the offsets used to infer the A-site will be applied to the read counts."
              />

              <CheckboxField
                id="aggregate_counts"
                checked={settings.aggregate_counts}
                onChange={onUpdateSetting}
                label="Aggregate counts"
                tooltip="If checked, the counts from all selected files will be grouped together, if unchecked files will be plotted with different colours seperately. A maximum of 6 files can be selected if not aggregating."
              />

              <Row>
                <Col md={6}>
                  <NumberField
                    id="hmap_strt"
                    value={settings.hmap_strt}
                    onChange={onUpdateSetting}
                    label="Start position"
                    placeholder="-300"
                    tooltip="Distance (in nucleotides), to display upstream of the start/stop codon."
                  />
                </Col>
                <Col md={6}>
                  <NumberField
                    id="hmap_stop"
                    value={settings.hmap_stop}
                    onChange={onUpdateSetting}
                    label="End position"
                    placeholder="300"
                    tooltip="Distance (in nucleotides), to display downstream of the start/stop codon."
                  />
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <NumberField
                    id="minread"
                    value={settings.minread}
                    onChange={onUpdateSetting}
                    label="Min readlen"
                    placeholder="20"
                    tooltip="The shortest read length displayed on the plot."
                    required
                  />
                </Col>
                <Col md={6}>
                  <NumberField
                    id="maxread"
                    value={settings.maxread}
                    onChange={onUpdateSetting}
                    label="Max readlen"
                    placeholder="35"
                    tooltip="The longest read length displayed on the plot."
                    required
                  />
                </Col>
              </Row>

              <TextField
                id="metagene_tranlist"
                value={settings.metagene_tranlist || ""}
                onChange={onUpdateSetting}
                label="Transcript list (optional)"
                placeholder=""
                type="textarea"
                rows="3"
                tooltip="Enter a comma separated list of transcript ids."
              />

              <RadioGroupVertical
                name="norm_method"
                options={CUSTOM_METAGENE_NORMALIZATION_METHODS}
                value={settings.norm_method}
                onChange={onUpdateSetting}
                label="Normalisation method"
                tooltip="If checked, counts will be normalised based on the number of mapped reads in each file. This is based on a factor difference between the file with lowest number of mapped reads and all other files. For example if plotting three files A,B and C which have 100,200,300 mapped reads respectively. The counts from file A would be unchanged (as it has the lowest number of mapped reads), all counts from file B woudld be multiplied by a factor of 0.5 (100/200 = 0.5) and all counts from File C would be multiplied by a factor of 0.33 (100/300 = 0.33)."
              />

            </Col>
            <Col md={6}>
              <SectionHeader title="Parameters" />

              {/* Custom sequence input and search limits */}
                <>
                  <TextField
                    id="custom_seq_list"
                    value={settings.custom_seq_list}
                    onChange={onUpdateSetting}
                    label="Custom sequence list"
                    type="textarea"
                    rows="2"
                    tooltip="Rather than centering the plot around annotated start or stop codons multiple custom sequences (seperated via comma) can be entered in this box. The Min/Max Limits limits below restrict where the custom sequences should be searched for, limits can be set to Transcript start, CDS start, CDS stop or Transcript end. For example to recreate the stop codon plot, UAG,UAA,UGA would be entered into the custom sequence list, Min limit should be set to 'CDS stop' with a -3 nt offset and Max limit should be set to 'CDS stop' with a 0 nt offset."
                  />

                  <NucleotideBounds
                    settings={settings}
                    onUpdateSetting={onUpdateSetting}
                  />
                </>
            </Col>
          </Row>
        );

      case PLOT_TYPES.MRNA_DIST:
        return (
          <Row>
            <Col md={6}>
              <SectionHeader title="Analysis Settings" />
              <RadioGroup
                name="count_type"
                options={COUNT_TYPES}
                value={settings.count_type}
                onChange={onUpdateSetting}
                label="Count type"
                tooltip="Choose either gene level or transcript level counts."
              />

              <CheckboxField
                id="per"
                checked={settings.per}
                onChange={onUpdateSetting}
                label="Percentage"
                tooltip="If checked, counts are shown as a percentage."
              />

              <RadioGroup
                name="mrna_dist_category"
                options={[
                  { value: "mrna_dist_file", label: "File" },
                  { value: "mrna_dist_region", label: "Region" },
                ]}
                value={settings.mrna_dist_category}
                onChange={onUpdateSetting}
                label="Categorise by"
                tooltip="Group counts by either file, or by region."
              />
            </Col>
          </Row>
        );

      case PLOT_TYPES.MRNA_DIST_READLEN:
        return (
          <Row>
            <Col md={6}>
              <SectionHeader title="Display Options" />
              <CheckboxField
                id="per"
                checked={settings.per}
                onChange={onUpdateSetting}
                label="Percentage"
                tooltip="If checked, each read length is displayed as a percentage of the total number of reads."
              />
            </Col>
          </Row>
        );

      case PLOT_TYPES.CODON_OCCUPANCY:
        return (
          <div className="text-center py-4">
            <div className="text-muted">
              <i
                className="ri-information-line"
                style={{ fontSize: "2rem", opacity: 0.5 }}
              />
              <p className="mt-2 mb-0">
                No additional options available for this plot type.
              </p>
            </div>
          </div>
        );

      case PLOT_TYPES.CLUSTER:
        return (
              <RadioGroup
                name="corr_type"
                options={CORRELATION_TYPES}
                value={settings.corr_type}
                onChange={onUpdateSetting}
                label="Correlation type"
                tooltip="Choose either Spearman or Pearson correlation."
              />
        );
      case PLOT_TYPES.QC_SUMMARY:
        return (
          <div className="text-center py-4">
            <div className="text-muted">
              <i
                className="ri-table-line"
                style={{ fontSize: "2rem", opacity: 0.5 }}
              />
              <p className="mt-2 mb-0">
                Provides a summary of results for key data quality metrics for all selected
                samples, each sample will be separated into PASS and WARN categories for 
                each test type. 
              </p>
            </div>
          </div>
        );

      case PLOT_TYPES.REPLICATE_COMP:
        return (
          <Row>
            <Col md={6}>
              <SectionHeader title="Analysis Settings" />
              <RadioGroup
                name="count_type"
                options={COUNT_TYPES}
                value={settings.count_type}
                onChange={onUpdateSetting}
                label="Count type"
                tooltip="This plot simply maps the counts for every transcript from two different files. This is useful for assessing technical/biological replicates. Ideally there should be a good correlation between replicates, showing a tight linear relationship between the two files. If this is not the case it could indicate an issue with one of the files. A maximum of 12 files can be selected if using this plot type."
              />

              <NumberField
                id="minread_count"
                value={settings.minread_count}
                onChange={onUpdateSetting}
                label="Minimum reads"
                placeholder="0"
                min={0}
                tooltip="Minimum number of reads required for a transcript to be included in the plot."
              />

              <RadioGroup
                name="corr_type"
                options={CORRELATION_TYPES}
                value={settings.corr_type}
                onChange={onUpdateSetting}
                label="Correlation type"
                tooltip="Choose either Spearman or Pearson correlation."
              />
            </Col>
          </Row>
        );

      case PLOT_TYPES.UNMAPPED:
        return (
          <div className="text-center py-4">
            <div className="text-muted">
              <i
                className="ri-table-line"
                style={{ fontSize: "2rem", opacity: 0.5 }}
              />
              <p className="mt-2 mb-0">
                This shows a sorted table of the top 100 most frequently
                unmapped reads in the files selected. Each read shows the number
                of times it appeared in the file and a link to the blast
                resource, where the read will be automatically filled into the
                search box. This table can be useful to detect contamination in
                samples.
              </p>
            </div>
          </div>
        );

      case PLOT_TYPES.TE:
        return (
          <Row>
            <Col md={6}>
              <SectionHeader title="Count Configuration" />
              <RadioGroup
                name="count_type"
                options={COUNT_TYPES}
                value={settings.count_type}
                onChange={onUpdateSetting}
                label="Count type"
                tooltip="Gene level counts will return UTR and CDS counts for each gene. Transcript level counts will return counts for each individual transcript."
              />

              <RadioGroup
                name="count_result"
                options={COUNT_RESULTS}
                value={settings.count_result}
                onChange={onUpdateSetting}
                label="Result"
                tooltip="Raw counts: No normalisation. RPKM (Reads Per Kilobase Million): Normalized for gene length and sequencing depth. TPM (Transcripts Per Million): Normalized for transcript length and sequencing depth."
              />

              <CheckboxField
                id="aggregate_counts"
                checked={settings.aggregate_counts}
                onChange={onUpdateSetting}
                label="Aggregate"
                tooltip="If checked, all counts for the same transcript will be aggregated if multiple files are selected."
              />
            </Col>
            <Col md={6}>
              <SectionHeader title="Gene/Transcript Selection" />
              <TextField
                id="te_genelist"
                value={settings.te_genelist}
                onChange={onUpdateSetting}
                label="Gene/Transcript list"
                placeholder="Enter comma or space separated list"
                type="textarea"
                rows="4"
                tooltip="Enter a comma or space separated list of genes or transcripts to restrict results to these entries."
              />
            </Col>
          </Row>
        );

      default:
        return (
          <div className="text-center py-5">
            <div className="text-muted">
              <i
                className="ri-settings-3-line"
                style={{ fontSize: "2rem", opacity: 0.5 }}
              />
              <p className="mt-2 mb-0">
                Select a plot type to view available options
              </p>
            </div>
          </div>
        );
    }
  }, [settings, onUpdateSetting]);

  // ✅ Enhanced main component render with consistent styling
  if (!plotType) {
    return null;
  }

  return (
    <Card
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: "4px",
        height: "100%",
        minHeight: "300px",
        transition: "box-shadow 0.15s ease-in-out",
      }}
    >
      <CardHeader className="d-flex justify-content-between align-items-center">
        <h5
          className="mb-0"
          style={{
            color: "#1a6d51",
            fontSize: "18px",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
          }}
        >
          <i className="ri-settings-3-line me-2" style={{ fontSize: "18px" }} />
          Plot Options
        </h5>
      </CardHeader>
      <CardBody style={{ padding: "20px" }}>
        {renderOptionsForPlotType()}
      </CardBody>
    </Card>
  );
};

// PropTypes
PlotOptions.propTypes = {
  plotType: PropTypes.string,
  settings: PropTypes.object.isRequired,
  onUpdateSetting: PropTypes.func.isRequired,
};

export default PlotOptions;
